import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { FaTimes } from 'react-icons/fa';
import dayjs from 'dayjs';
import Modal, { Field } from '../../common/Modal';
import { employeesApi } from '../../../services/api';
import { useAlert } from '../../../contexts/AlertContext';
import { fromPickerValue } from '../../../utils/dateOnly';

/**
 * Modal for ending an employee's organizational role
 *
 * @param {Object} props
 * @param {Boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Object} props.role - The role being ended
 * @param {String} props.employeeId - The employee ID
 * @param {Function} props.onSuccess - Callback when role end is successful
 */
const EndRoleModal = ({
  isOpen,
  onClose,
  role,
  employeeId,
  onSuccess
}) => {
  const { showAlert } = useAlert();
  const [endDate, setEndDate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setEndDate(dayjs());
    }
  }, [isOpen]);

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      await employeesApi.endRoleHistory(
        employeeId,
        role.id,
        fromPickerValue(endDate) ?? undefined
      );

      showAlert('Role ended successfully', 'success');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error ending role:', error);
      showAlert(error.message || 'Error ending role', 'error', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  if (!role) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="End Organizational Role"
      onSubmit={handleSubmit}
      submitText={isSubmitting ? 'Ending Role...' : 'End Role'}
      submitDisabled={!endDate || isSubmitting}
      submitButtonProps={{
        sx: { backgroundColor: '#2563EB', color: '#FFFFFF', '&:hover': { backgroundColor: '#1E40AF' } }
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <Box>
          <Typography variant="body1" sx={{ color: '#64748B' }}>
            You are ending the role:
          </Typography>
          <Typography variant="h6" sx={{ color: '#1E293B', fontWeight: 600, mt: 1 }}>
            {role.label}
          </Typography>
        </Box>

        <Field>
          <Typography variant="subtitle1" gutterBottom>
            End Date
          </Typography>
          <DatePicker
            value={endDate}
            onChange={(newValue) => {
              if (newValue && typeof newValue.isValid === 'function' && newValue.isValid()) {
                setEndDate(newValue);
              } else if (newValue === null) {
                setEndDate(null);
              }
            }}
            format="DD/MM/YYYY"
            disabled={isSubmitting}
            slotProps={{
              textField: {
                fullWidth: true,
                placeholder: "When should this role end?",
                size: "medium",
                required: true,
                inputProps: {
                  placeholder: 'DD/MM/YYYY'
                }
              }
            }}
          />
          {endDate && (
            <Box sx={{ mt: 1 }}>
              <Typography
                variant="body2"
                color="primary"
                sx={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                onClick={() => setEndDate(null)}
              >
                <FaTimes size={12} /> Clear date
              </Typography>
            </Box>
          )}
        </Field>
      </Box>
    </Modal>
  );
};

export default EndRoleModal;
